import React from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useAuth } from "../auth/authStore";
import { useNotifications } from "../notifications/notificationsStore";
import { psychologistClientsApi } from "../api/psychologistClientsApi";
import { useToast } from "../ui/toast/ToastProvider";
import { DIARY_MOOD_OPTIONS, diaryMoodBadge } from "../utils/diary";

const NOTE_TYPES = Object.freeze([
  { key: "observation", label: "Наблюдение", badgeClass: "is-observation", matchLabels: ["наблюдение"] },
  { key: "hypothesis", label: "Гипотеза", badgeClass: "is-hypothesis", matchLabels: ["гипотеза"] },
  { key: "session", label: "Сессия", badgeClass: "is-session", matchLabels: ["сессия"] },
  { key: "plan", label: "Задача", badgeClass: "is-plan", matchLabels: ["задача", "план"] },
]);

function normalizeRoleToken(value) {
  return String(value || "").replace(/^ROLE_/, "").trim().toUpperCase();
}

function roleLabel(value) {
  const token = normalizeRoleToken(value);
  if (token === "ADMIN" || token === "SUPER_ADMIN") return "Администратор";
  if (token === "PSYCHOLOGIST" || token === "PSYCHO") return "Психолог";
  if (token === "MODERATOR") return "Модератор";
  return "Специалист";
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;
  const obj = asObject(raw);
  if (Array.isArray(obj.content)) return obj.content;
  if (Array.isArray(obj.items)) return obj.items;
  if (Array.isArray(obj.items?.content)) return obj.items.content;
  if (Array.isArray(obj.item?.items)) return obj.item.items;
  if (Array.isArray(obj.item?.content)) return obj.item.content;
  if (Array.isArray(obj.item)) return obj.item;
  return [];
}

function firstDefined(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && !value.trim()) continue;
    return value;
  }
  return null;
}

function parseDate(value) {
  if (!value) return null;
  const parsed = value instanceof Date ? new Date(value) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value, options = {}) {
  const parsed = parseDate(value);
  if (!parsed) return "—";
  return new Intl.DateTimeFormat("ru-RU", options).format(parsed);
}

function formatDateTime(value) {
  return formatDate(value, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function capitalizeFirst(value) {
  const text = String(value || "").trim();
  return text ? `${text.slice(0, 1).toUpperCase()}${text.slice(1)}` : "";
}

function initials(value = "") {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  const joined = parts.map((part) => part[0]).join("");
  return joined ? joined.toUpperCase() : "БД";
}

function firstName(value = "") {
  return String(value || "").trim().split(/\s+/).filter(Boolean)[0] || "";
}

function noteCountLabel(value) {
  const count = Number(value);
  if (!Number.isFinite(count)) return "";
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} заметка`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} заметки`;
  return `${count} заметок`;
}

function toClientId(raw = {}) {
  const src = asObject(raw);
  return firstDefined(
    src.clientUserId,
    src.clientId,
    src.userId,
    src.user?.id,
    src.client?.userId,
    src.client?.id,
    src.id
  );
}

function normalizeClient(rawClient = {}) {
  const raw = asObject(rawClient);
  const clientUserId = toClientId(raw);
  const name = String(
    firstDefined(
      raw.name,
      raw.fullName,
      raw.clientName,
      raw.displayName,
      raw.user?.name,
      raw.client?.name,
      raw.email,
      raw.user?.email,
      `Клиент #${clientUserId || "—"}`
    ) || "Клиент"
  ).trim();
  const contact = String(firstDefined(raw.phone, raw.email, raw.contact, raw.user?.email, raw.telegram, "—") || "—").trim();
  const topic = String(firstDefined(raw.topic, raw.requestTopic, raw.mainRequest, raw.goal, raw.tags?.[0], "Запрос не указан") || "Запрос не указан").trim();
  const note = String(firstDefined(raw.note, raw.lastNote, raw.shortComment, raw.description, "Заметка пока не добавлена") || "").trim();
  const lastSessionAt = firstDefined(raw.lastSessionAt, raw.lastAppointmentAt, raw.lastVisitAt, raw.updatedAt, raw.lastMessageAt);
  const nextSessionAt = firstDefined(raw.nextSessionAt, raw.nextAppointmentAt, raw.closestBookingAt, raw.upcomingSessionAt);
  const status = String(firstDefined(raw.status, raw.lifecycleStatus, raw.state, "ACTIVE") || "ACTIVE").toUpperCase();

  return {
    id: String(clientUserId || raw.id || name),
    clientUserId: clientUserId || raw.id || null,
    name,
    initials: initials(name),
    contact,
    topic,
    note,
    status,
    lastSessionAt,
    nextSessionAt,
    sessionsCount: Number(firstDefined(raw.sessionsCount, raw.completedSessionsCount, raw.bookingsCount, raw.appointmentsCount, 0)) || 0,
    notesCount: firstDefined(raw.notesCount, raw.privateNotesCount, raw.clientNotesCount, raw.notesTotal, raw.noteCount),
  };
}

function noteTypeFromLabel(label = "") {
  const text = String(label || "").trim().toLowerCase();
  return NOTE_TYPES.find((item) => (item.matchLabels || []).some((token) => text.includes(token))) || NOTE_TYPES[0];
}

function trimText(value, max = 240) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

function moodEmoji(score) {
  const parsed = Number(score);
  const option = DIARY_MOOD_OPTIONS.find((item) => Number(item.score) === parsed);
  return option?.emoji || "•";
}

function clientMatchesQuery(client, query) {
  const normalized = String(query || "").trim().toLowerCase();
  if (!normalized) return true;
  return [client.name, client.contact, client.topic, client.note, client.status]
    .join(" ")
    .toLowerCase()
    .includes(normalized);
}

function normalizeApiErrorToken(error) {
  return String(error?.message || error?.code || error?.error || "").trim().toUpperCase();
}

function resolveDiaryErrorMessage(error) {
  const raw = normalizeApiErrorToken(error);
  if (raw.includes("DIARY_ACCESS_NOT_GRANTED")) {
    return "Клиент пока не открыл доступ к дневнику или не поделился записями.";
  }
  if (raw.includes("NO_CLIENT_RELATION")) {
    return "Дневник доступен только после оплаченной или завершённой сессии с клиентом.";
  }
  return "Клиент пока не открыл дневник или доступ временно недоступен.";
}

function resolveNotesErrorMessage(error) {
  const raw = normalizeApiErrorToken(error);
  if (raw.includes("NO_CLIENT_RELATION")) {
    return "Заметки доступны только по клиентам с историей оплаченных или завершённых сессий.";
  }
  if (raw.includes("DATABASE ERROR")) {
    return "Не удалось загрузить заметки. Попробуйте чуть позже.";
  }
  return "Не удалось загрузить заметки.";
}

export default function PsychologistNotes() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me, logout } = useAuth();
  const { unreadNotificationsCount = 0 } = useNotifications();
  const toast = useToast();

  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [clients, setClients] = React.useState([]);
  const [clientsLoading, setClientsLoading] = React.useState(true);
  const [clientsError, setClientsError] = React.useState("");
  const [clientQuery, setClientQuery] = React.useState("");
  const [selectedClientId, setSelectedClientId] = React.useState(() => searchParams.get("clientId") || "");
  const [activeTab, setActiveTab] = React.useState("notes");
  const [notes, setNotes] = React.useState([]);
  const [notesLoading, setNotesLoading] = React.useState(false);
  const [notesError, setNotesError] = React.useState("");
  const [diaryEntries, setDiaryEntries] = React.useState([]);
  const [diaryLoading, setDiaryLoading] = React.useState(false);
  const [diaryError, setDiaryError] = React.useState("");
  const [noteType, setNoteType] = React.useState(NOTE_TYPES[0].key);
  const [noteDraft, setNoteDraft] = React.useState("");
  const [editingNoteId, setEditingNoteId] = React.useState("");
  const [savingNote, setSavingNote] = React.useState(false);
  const [deletingNoteId, setDeletingNoteId] = React.useState("");

  const userName = me?.name || me?.fullName || me?.login || me?.email || "Специалист";
  const roleToken = normalizeRoleToken(me?.role);
  const isPsychologist = roleToken === "PSYCHOLOGIST" || roleToken === "PSYCHO";
  const canManageLiterature = ["ADMIN", "SUPER_ADMIN", "PSYCHOLOGIST", "PSYCHO", "MODERATOR"].includes(roleToken);

  const filteredClients = React.useMemo(
    () => clients.filter((client) => clientMatchesQuery(client, clientQuery)),
    [clientQuery, clients]
  );

  const selectedClient = React.useMemo(() => {
    if (!clients.length) return null;
    return clients.find((client) => String(client.clientUserId || client.id) === String(selectedClientId)) || clients[0];
  }, [clients, selectedClientId]);

  const selectedClientApiId = selectedClient?.clientUserId || selectedClient?.id || "";
  const selectedType = NOTE_TYPES.find((item) => item.key === noteType) || NOTE_TYPES[0];

  const handleLogout = React.useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const syncSelectedClientParam = React.useCallback(
    (clientId) => {
      const id = String(clientId || "").trim();
      if (!id) return;
      const next = new URLSearchParams(searchParams);
      next.set("clientId", id);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const loadClients = React.useCallback(async () => {
    setClientsLoading(true);
    setClientsError("");
    try {
      const response = await psychologistClientsApi.listItems({ page: 0, size: 100 });
      const normalized = extractItems(response?.items ?? response?.raw ?? response).map(normalizeClient);
      setClients(normalized);
      const requestedId = searchParams.get("clientId");
      const exists = normalized.some((client) => String(client.clientUserId || client.id) === String(requestedId));
      if (requestedId && exists) {
        setSelectedClientId(requestedId);
      } else if (normalized[0]) {
        const firstId = String(normalized[0].clientUserId || normalized[0].id);
        setSelectedClientId(firstId);
        const next = new URLSearchParams(searchParams);
        next.set("clientId", firstId);
        setSearchParams(next, { replace: true });
      }
    } catch (error) {
      setClients([]);
      setClientsError(error?.message || "Не удалось загрузить клиентов");
    } finally {
      setClientsLoading(false);
    }
  }, [searchParams, setSearchParams]);

  const loadClientData = React.useCallback(async (clientId) => {
    const id = String(clientId || "").trim();
    if (!id) {
      setNotes([]);
      setDiaryEntries([]);
      return;
    }

    setNotesLoading(true);
    setDiaryLoading(true);
    setNotesError("");
    setDiaryError("");

    try {
      const loadedNotes = await psychologistClientsApi.listClientPrivateNotes(id, { page: 0, size: 100 });
      setNotes(Array.isArray(loadedNotes) ? loadedNotes : []);
    } catch (error) {
      setNotes([]);
      setNotesError(resolveNotesErrorMessage(error));
    } finally {
      setNotesLoading(false);
    }

    try {
      const loadedDiary = await psychologistClientsApi.listClientDiaryEntries(id, { page: 0, size: 50 });
      setDiaryEntries(Array.isArray(loadedDiary) ? loadedDiary : []);
    } catch (error) {
      setDiaryEntries([]);
      setDiaryError(resolveDiaryErrorMessage(error));
    } finally {
      setDiaryLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadClients();
  }, [loadClients]);

  React.useEffect(() => {
    if (!selectedClientApiId) return;
    loadClientData(selectedClientApiId);
  }, [loadClientData, selectedClientApiId]);

  const handleSelectClient = React.useCallback(
    (client) => {
      const id = String(client?.clientUserId || client?.id || "").trim();
      if (!id) return;
      setSelectedClientId(id);
      syncSelectedClientParam(id);
      setActiveTab("notes");
      setEditingNoteId("");
      setNoteDraft("");
      setNoteType(NOTE_TYPES[0].key);
    },
    [syncSelectedClientParam]
  );

  const handleEditNote = React.useCallback((note) => {
    if (!note) return;
    const type = noteTypeFromLabel(note.shortLabel);
    setEditingNoteId(String(note.id || ""));
    setNoteType(type.key);
    setNoteDraft(String(note.note || ""));
    setActiveTab("notes");
  }, []);

  const resetNoteDraft = React.useCallback(() => {
    setEditingNoteId("");
    setNoteDraft("");
    setNoteType(NOTE_TYPES[0].key);
  }, []);

  const handleSaveNote = React.useCallback(async () => {
    const clientId = String(selectedClientApiId || "").trim();
    const content = String(noteDraft || "").trim();
    if (!clientId) {
      toast.error("Выберите клиента");
      return;
    }
    if (!content) {
      toast.error("Добавьте текст заметки");
      return;
    }

    setSavingNote(true);
    try {
      const payload = { shortLabel: selectedType.label, note: content };
      if (editingNoteId) {
        await psychologistClientsApi.updateClientPrivateNote(clientId, editingNoteId, payload);
        toast.success("Заметка обновлена");
      } else {
        await psychologistClientsApi.createClientPrivateNote(clientId, payload);
        toast.success("Заметка добавлена");
      }
      resetNoteDraft();
      const loadedNotes = await psychologistClientsApi.listClientPrivateNotes(clientId, { page: 0, size: 100 });
      setNotes(Array.isArray(loadedNotes) ? loadedNotes : []);
    } catch (error) {
      toast.error(error?.message || "Не удалось сохранить заметку");
    } finally {
      setSavingNote(false);
    }
  }, [editingNoteId, noteDraft, resetNoteDraft, selectedClientApiId, selectedType.label, toast]);

  const handleDeleteNote = React.useCallback(
    async (note) => {
      const clientId = String(selectedClientApiId || "").trim();
      const noteId = String(note?.id || "").trim();
      if (!clientId || !noteId) return;
      const confirmed = window.confirm("Удалить заметку по клиенту?");
      if (!confirmed) return;
      setDeletingNoteId(noteId);
      try {
        await psychologistClientsApi.deleteClientPrivateNote(clientId, noteId);
        setNotes((prev) => prev.filter((item) => String(item.id) !== noteId));
        if (String(editingNoteId || "") === noteId) resetNoteDraft();
        toast.success("Заметка удалена");
      } catch (error) {
        toast.error(error?.message || "Не удалось удалить заметку");
      } finally {
        setDeletingNoteId("");
      }
    },
    [editingNoteId, resetNoteDraft, selectedClientApiId, toast]
  );

  if (!isPsychologist) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="client-dashboard-v2 psychologist-notes-v3 podcasts-hub">
      <Helmet prioritizeSeoTags>
        <title>Заметки по клиентам | Бюро Души</title>
        <meta
          name="description"
          content="Рабочие заметки психолога по клиентам, приватные наблюдения и записи дневника, открытые клиентом."
        />
      </Helmet>

      <div className="shell shell--shared podcasts-shell">
        <AppSidebarV2
          activeKey="notes"
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          userName={userName}
          roleLabel={roleLabel(me?.role)}
          canOpenLiteratureEditor={canManageLiterature}
          notificationsBadge={unreadNotificationsCount}
          onLogout={handleLogout}
        />

        <button
          className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
          type="button"
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Закрыть меню"
        />

        <main className="main podcasts-main pn-main">
          <div className="topbar psychologist-notes-v3__topbar">
            <div className="topbar-left psychologist-notes-v3__topbar-left">
              <button
                type="button"
                className="btn-icon topbar-hamburger"
                onClick={() => setMobileSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <div className="psychologist-notes-v3__topbar-title-wrap">
                <div className="topbar-title psychologist-notes-v3__topbar-title">Заметки по клиентам</div>
              </div>
            </div>
          </div>

          <div className="content psychologist-notes-v3__content">
            <div className="psychologist-notes-v3__layout">
              <aside className="psychologist-notes-v3__clients-panel">
                <div className="psychologist-notes-v3__clients-head">
                  <div>
                    <div className="psychologist-notes-v3__panel-title">Клиенты</div>
                    <div className="psychologist-notes-v3__panel-subtitle">{filteredClients.length} из {clients.length}</div>
                  </div>
                  <div className="psychologist-notes-v3__search">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input
                      type="text"
                      value={clientQuery}
                      onChange={(event) => setClientQuery(event.target.value)}
                      placeholder="Найти клиента..."
                    />
                  </div>
                </div>

                <div className="psychologist-notes-v3__clients-list">
                  {clientsLoading ? <div className="psychologist-notes-v3__empty-list">Загружаем клиентов…</div> : null}
                  {!clientsLoading && clientsError ? <div className="psychologist-notes-v3__empty-list is-error">{clientsError}</div> : null}
                  {!clientsLoading && !clientsError && !filteredClients.length ? <div className="psychologist-notes-v3__empty-list">Клиенты не найдены</div> : null}
                  {!clientsLoading &&
                    !clientsError &&
                    filteredClients.map((client) => {
                      const id = String(client.clientUserId || client.id);
                      const active = String(selectedClientApiId) === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          className={`psychologist-notes-v3__client-item ${active ? "is-active" : ""}`}
                          onClick={() => handleSelectClient(client)}
                        >
                          <span className="psychologist-notes-v3__client-avatar">{client.initials}</span>
                          <span className="psychologist-notes-v3__client-meta">
                            <span className="psychologist-notes-v3__client-name">{client.name}</span>
                            <span className="psychologist-notes-v3__client-sub">
                              {Number.isFinite(Number(client.notesCount))
                                ? noteCountLabel(client.notesCount)
                                : client.nextSessionAt
                                  ? `Сессия ${formatDate(client.nextSessionAt, { day: "2-digit", month: "short" })}`
                                  : client.topic}
                            </span>
                          </span>
                          <span className={`psychologist-notes-v3__client-dot ${diaryEntries.length && active ? "has-diary" : ""}`} />
                        </button>
                      );
                    })}
                </div>
              </aside>

              <section className="psychologist-notes-v3__workspace">
                {selectedClient ? (
                  <>
                    <section className={`psychologist-notes-v3__diary-banner ${diaryError ? "is-locked" : ""}`}>
                      <div className="psychologist-notes-v3__diary-icon">{diaryError ? "🔒" : "📘"}</div>
                      <div className="psychologist-notes-v3__diary-text">
                        <strong>{diaryError ? "Дневник недоступен" : `${firstName(selectedClient.name) || "Клиент"} открыл(а) вам доступ к дневнику`}</strong>
                        <span>
                          {diaryError
                            ? diaryError
                            : "Вы можете видеть записи, которые клиент отметил как открытые. Закрытые записи остаются только у него."}
                        </span>
                      </div>
                      <button type="button" className="btn-sm" onClick={() => setActiveTab("diary")}>Смотреть</button>
                    </section>

                    <div className="psychologist-notes-v3__tabs" role="tablist" aria-label="Заметки и дневник">
                      <button
                        type="button"
                        className={activeTab === "notes" ? "is-active" : ""}
                        onClick={() => setActiveTab("notes")}
                      >
                        Мои заметки <span>{notes.length}</span>
                      </button>
                      <button
                        type="button"
                        className={activeTab === "diary" ? "is-active" : ""}
                        onClick={() => setActiveTab("diary")}
                      >
                        Дневник клиента <span>{diaryEntries.length}</span>
                      </button>
                    </div>

                    <section className="psychologist-notes-v3__tab-card">
                      {activeTab === "notes" ? (
                        <>
                          <div className="psychologist-notes-v3__composer">
                            <div className="psychologist-notes-v3__composer-head">
                              <div className="psychologist-notes-v3__composer-label">
                                {editingNoteId
                                  ? `Редактирование заметки — ${selectedClient.name}`
                                  : `Новая заметка — ${selectedClient.name}`}
                              </div>
                              <div className="psychologist-notes-v3__type-selector">
                                {NOTE_TYPES.map((type) => (
                                  <button
                                    key={type.key}
                                    type="button"
                                    className={noteType === type.key ? "is-active" : ""}
                                    onClick={() => setNoteType(type.key)}
                                  >
                                    {type.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <textarea
                              value={noteDraft}
                              onChange={(event) => setNoteDraft(event.target.value)}
                              placeholder="Заметки видны только вам. Записывайте наблюдения, гипотезы, динамику клиента..."
                              rows={5}
                              maxLength={10000}
                            />
                            <div className="psychologist-notes-v3__composer-footer">
                              <div>
                                {editingNoteId ? <button type="button" className="btn-sm" onClick={resetNoteDraft}>Отмена</button> : null}
                                <button type="button" className="btn-sm primary" onClick={handleSaveNote} disabled={savingNote || !noteDraft.trim()}>
                                  {savingNote ? "Сохраняем…" : editingNoteId ? "Сохранить заметку" : "Сохранить заметку"}
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="psychologist-notes-v3__list-label">Предыдущие заметки</div>
                          <div className="psychologist-notes-v3__notes-list">
                            {notesLoading ? <div className="psychologist-notes-v3__empty-state">Загружаем заметки…</div> : null}
                            {!notesLoading && notesError ? <div className="psychologist-notes-v3__empty-state is-error">{notesError}</div> : null}
                            {!notesLoading && !notesError && !notes.length ? <div className="psychologist-notes-v3__empty-state">По этому клиенту пока нет заметок.</div> : null}
                            {!notesLoading &&
                              !notesError &&
                              notes.map((note) => {
                                const type = noteTypeFromLabel(note.shortLabel);
                                const noteId = String(note.id || "");
                                return (
                                  <article className="psychologist-notes-v3__note-card" key={noteId || `${note.createdAt}-${note.note}`}>
                                    <div className="psychologist-notes-v3__note-top">
                                      <div className="psychologist-notes-v3__note-meta">
                                        <span className={`psychologist-notes-v3__note-badge ${type.badgeClass}`}>{note.shortLabel || type.label}</span>
                                        <span>{formatDateTime(note.updatedAt || note.createdAt)}</span>
                                      </div>
                                      <div className="psychologist-notes-v3__note-actions">
                                        <button type="button" className="psychologist-notes-v3__icon-btn" onClick={() => handleEditNote(note)} aria-label="Редактировать заметку" title="Редактировать">
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                          </svg>
                                        </button>
                                        <button
                                          type="button"
                                          className="psychologist-notes-v3__icon-btn"
                                          onClick={() => handleDeleteNote(note)}
                                          disabled={deletingNoteId === noteId}
                                          aria-label="Удалить заметку"
                                          title="Удалить"
                                        >
                                          {deletingNoteId === noteId ? (
                                            "…"
                                          ) : (
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                              <polyline points="3 6 5 6 21 6" />
                                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                            </svg>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                    <p>{note.note}</p>
                                  </article>
                                );
                              })}
                          </div>
                        </>
                      ) : (
                        <div className="psychologist-notes-v3__diary-tab">
                          <section className="psychologist-notes-v3__diary-banner psychologist-notes-v3__diary-banner--inline">
                            <div className="psychologist-notes-v3__diary-icon">👁</div>
                            <div className="psychologist-notes-v3__diary-text">
                              <strong>Видны только записи с открытым доступом</strong>
                              <span>Клиент самостоятельно выбирает, что открывать. Вы не можете видеть все записи дневника.</span>
                            </div>
                          </section>
                          <div className="psychologist-notes-v3__diary-list">
                          {diaryLoading ? <div className="psychologist-notes-v3__empty-state">Загружаем дневник…</div> : null}
                          {!diaryLoading && diaryError ? <div className="psychologist-notes-v3__empty-state is-locked">{diaryError}</div> : null}
                          {!diaryLoading && !diaryError && !diaryEntries.length ? <div className="psychologist-notes-v3__empty-state">Клиент пока не открыл записи дневника.</div> : null}
                          {!diaryLoading &&
                            !diaryError &&
                            diaryEntries.map((entry) => {
                              const badge = diaryMoodBadge(entry.moodScore);
                              return (
                                <article className="psychologist-notes-v3__diary-entry" key={entry.id || `${entry.entryDate}-${entry.title}`}>
                                  <div className="psychologist-notes-v3__diary-entry-top">
                                    <div className="psychologist-notes-v3__diary-entry-left">
                                      <span className="psychologist-notes-v3__diary-mood">{moodEmoji(entry.moodScore)}</span>
                                      <span>
                                        <strong>{capitalizeFirst(formatDate(entry.entryDate || entry.createdAt, { weekday: "long" }))}</strong>
                                        <small>{formatDate(entry.entryDate || entry.createdAt, { day: "numeric", month: "long", year: "numeric" })}</small>
                                      </span>
                                    </div>
                                    <span className="psychologist-notes-v3__diary-from">{badge.label || "Дневник"}</span>
                                  </div>
                                  <p>{trimText(entry.content || entry.text, 800)}</p>
                                </article>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </section>
                  </>
                ) : (
                  <section className="psychologist-notes-v3__client-context psychologist-notes-v3__client-context--empty">
                    <div>
                      <h1>Клиенты не найдены</h1>
                      <p>Добавьте клиента в разделе «Клиенты», чтобы вести приватные заметки и читать открытые записи дневника.</p>
                    </div>
                    <button type="button" className="btn-sm primary" onClick={() => navigate("/dashboard/clients")}>Перейти к клиентам</button>
                  </section>
                )}
              </section>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
